import { RoolClient } from '@rool-dev/sdk';
import type { RoolChannel, RoolSpace, RoolSpaceInfo } from '@rool-dev/sdk';
import { get } from 'svelte/store';
import {
	channel,
	spaceAdmin,
	spaces,
	roolClient,
	isInitializing,
	interactionsUpdateTrigger,
	currentUser,
	theme,
	gridStyle,
	gridSnapping,
	conversations,
	promptHistory,
} from '../stores';
import type { GridStyle } from '@rool/renderer';
import { rebuildGraph, clearPreloadedUrls, waitForMediaPreload } from './graph';
import { clearObjectCache, loadAllObjects } from './canvas-object-store';
import { setupCanvasReconciler } from './canvas-reconciler';
import { SYSTEM_PROMPT } from './constants';
import { updateUrlWithSpaceId } from './url-state';
import { clearMediaCache } from './media';
import { ensureSchema } from './schema';
import * as presence from './presence';

// Injected at build time by vite.config.js when ROOL_CREDENTIALS env var is set
declare const __ROOL_CREDENTIALS__:
	| { access_token: string; refresh_token: string; expires_at: number }
	| undefined;

/**
 * Inject CLI credentials into localStorage so the SDK's BrowserAuthProvider
 * picks them up automatically. Only runs once per page load.
 */
let credentialsInjected = false;
function injectCliCredentials(): void {
	if (credentialsInjected) return;
	credentialsInjected = true;

	if (typeof __ROOL_CREDENTIALS__ === 'undefined' || !__ROOL_CREDENTIALS__)
		return;

	const creds = __ROOL_CREDENTIALS__;
	const prefix = 'rool_';

	// Only inject if not already authenticated (don't overwrite browser login)
	if (localStorage.getItem(`${prefix}access_token`)) return;

	localStorage.setItem(`${prefix}access_token`, creds.access_token);
	localStorage.setItem(`${prefix}refresh_token`, creds.refresh_token);
	localStorage.setItem(
		`${prefix}token_expires_at`,
		Math.floor(creds.expires_at).toString(),
	);
	console.log('[rool] Injected CLI credentials into localStorage');
}

export function getRoolBaseUrl(): string {
	if (typeof window !== 'undefined') {
		const hostname = window.location.hostname;
		if (hostname === 'canvas.rool.app') {
			return 'https://api.rool.dev';
		}
	}
	return 'https://api.dev.rool.dev';
}

export async function initializeSpace(
	spaceId?: string,
): Promise<{ space: RoolChannel; isNew: boolean } | null> {
	injectCliCredentials();

	const client = new RoolClient({
		baseUrl: getRoolBaseUrl(),
	});

	await client.initialize();
	roolClient.set(client);

	const isAuthenticated = await client.isAuthenticated();

	if (!isAuthenticated) {
		client.login('Rool Canvas');
		return null;
	}

	// Use the user already fetched during client.initialize() (avoids duplicate getAccount call)
	const cachedUser = client.currentUser;
	if (cachedUser) {
		currentUser.set(cachedUser);
	}

	// Load persisted user preferences (server is authoritative, update localStorage cache)
	const savedTheme = client.getUserStorage<'light' | 'dark'>('theme');
	if (savedTheme === 'light' || savedTheme === 'dark') {
		theme.set(savedTheme);
		localStorage.setItem('theme', savedTheme);
	}

	const validGridStyles: GridStyle[] = [
		'dots',
		'lines',
		'hex',
		'dither',
		'none',
	];
	const savedGridStyle = client.getUserStorage<GridStyle>('gridStyle');
	if (savedGridStyle && validGridStyles.includes(savedGridStyle)) {
		gridStyle.set(savedGridStyle);
	}

	const savedGridSnapping = client.getUserStorage<boolean>('gridSnapping');
	if (typeof savedGridSnapping === 'boolean') {
		gridSnapping.set(savedGridSnapping);
	}

	// Sync preference changes from other tabs/devices
	client.on('userStorageChanged', ({ key, value }) => {
		if (key === 'theme' && (value === 'light' || value === 'dark')) {
			theme.set(value);
			localStorage.setItem('theme', value);
		}
		if (
			key === 'gridStyle' &&
			typeof value === 'string' &&
			validGridStyles.includes(value as GridStyle)
		) {
			gridStyle.set(value as GridStyle);
		}
		if (key === 'gridSnapping' && typeof value === 'boolean') {
			gridSnapping.set(value);
		}
	});

	// Listen for channel lifecycle events to keep conversation list updated
	client.on('channelCreated', () => refreshConversations());
	client.on('channelRenamed', () => refreshConversations());
	client.on('channelDeleted', () => refreshConversations());

	// Load available spaces
	await refreshSpaces();

	let openedChannel: RoolChannel;
	let openedSpace: RoolSpace;
	let isNew = false;

	try {
		if (spaceId) {
			// Try to open existing space by ID
			openedSpace = await client.openSpace(spaceId);
			openedChannel = await client.openChannel(spaceId, 'default');
		} else {
			// No spaceId provided - look for or create 'default' space
			const spaceList = get(spaces);
			const defaultSpace = spaceList.find((s) => s.name === 'default');

			if (defaultSpace) {
				// Open existing default space
				openedSpace = await client.openSpace(defaultSpace.id);
				openedChannel = await client.openChannel(defaultSpace.id, 'default');
			} else {
				// Create new default space
				openedSpace = await client.createSpace('default');
				openedChannel = await client.openChannel(openedSpace.id, 'default');
				isNew = true;
				await refreshSpaces();
			}
		}
	} catch (error) {
		console.error('Error opening/creating space:', error);
		// If opening failed (e.g., space doesn't exist), fall back to an existing default or create one
		try {
			await refreshSpaces();
			const spaceList = get(spaces);
			const defaultSpace = spaceList.find((s) => s.name === 'default');

			if (defaultSpace) {
				openedSpace = await client.openSpace(defaultSpace.id);
				openedChannel = await client.openChannel(defaultSpace.id, 'default');
			} else {
				openedSpace = await client.createSpace('default');
				openedChannel = await client.openChannel(openedSpace.id, 'default');
				isNew = true;
				await refreshSpaces();
			}
		} catch (fallbackError) {
			console.error('Failed to open/create fallback space:', fallbackError);
			isInitializing.set(false);
			throw fallbackError;
		}
	}

	await ensureConversation(openedChannel, openedSpace);
	await ensureSystemInstruction(openedChannel);
	await ensureSchema(openedChannel);

	// Clear caches when switching spaces
	clearMediaCache();
	clearPreloadedUrls();
	clearObjectCache();

	channel.set(openedChannel);
	spaceAdmin.set(openedSpace);
	setupChannelEventHandlers(openedChannel);

	// Fetch all objects into local cache, then rebuild graph synchronously
	await loadAllObjects(openedChannel);
	rebuildGraph();
	refreshConversations();
	seedPromptHistory(openedChannel);

	// Wait for media (images) to finish loading before hiding the splash screen.
	// Cap at 5s so the splash doesn't get stuck if a request hangs.
	await Promise.race([
		waitForMediaPreload(),
		new Promise<void>((r) => setTimeout(r, 5000)),
	]);

	// Update URL with current space
	updateUrlWithSpaceId(openedChannel.id);

	// Start broadcasting presence
	const user = get(currentUser);
	if (user) {
		presence.start(openedChannel, openedSpace, user.id, user);
	}

	isInitializing.set(false);

	return { space: openedChannel, isNew };
}

async function ensureConversation(
	ch: RoolChannel,
	space: RoolSpace,
): Promise<void> {
	// With the channel refactor, opening a channel with a conversationId
	// auto-creates the conversation on the server if it doesn't exist.
	// We just need to make sure it has a name — skip the server call if it already does.
	const channels = space.getChannels();
	const info = channels.find((c: { id: string }) => c.id === ch.channelId);
	if (info?.name) return;

	try {
		await ch.rename('Default');
	} catch {
		// Ignore — may already be named
	}
}

/**
 * Only set the system instruction if it differs from what the server already has.
 * The current instruction is available locally from the openChannel response.
 */
async function ensureSystemInstruction(ch: RoolChannel): Promise<void> {
	const current = ch.getSystemInstruction();
	if (current === SYSTEM_PROMPT) return;
	await ch.setSystemInstruction(SYSTEM_PROMPT);
}

/** Seed promptHistory from existing interactions so Arrow Up/Down works after page load */
function seedPromptHistory(ch: RoolChannel): void {
	const interactions = ch.getInteractions();
	const prompts = interactions
		.filter((i: any) => i.operation === 'prompt')
		.map((i: any) => {
			const input: string = i.input ?? '';
			const userIndex = input.indexOf('[USER]');
			return userIndex !== -1
				? input.slice(userIndex + '[USER]'.length).trim()
				: input.trim();
		})
		.filter((text: string) => text.length > 0);
	promptHistory.seed(prompts);
}

function setupChannelEventHandlers(ch: RoolChannel): void {
	setupCanvasReconciler(ch);

	ch.on('channelUpdated', () => {
		interactionsUpdateTrigger.update((n) => n + 1);
	});
}

export async function refreshSpaces(): Promise<RoolSpaceInfo[]> {
	const client = get(roolClient);
	if (!client) return [];

	const spaceList = await client.listSpaces();
	spaces.set(spaceList);
	return spaceList;
}

export async function switchSpace(
	spaceId: string,
): Promise<RoolChannel | null> {
	const client = get(roolClient);
	if (!client) return null;

	// Stop presence for current space
	presence.stop();

	// Close current channel
	const currentChannel = get(channel);
	if (currentChannel) {
		currentChannel.close();
	}

	// Clear caches when switching spaces
	clearMediaCache();
	clearPreloadedUrls();
	clearObjectCache();

	// Open new space admin + channel
	const openedSpace = await client.openSpace(spaceId);
	const openedChannel = await client.openChannel(spaceId, 'default');
	await ensureConversation(openedChannel, openedSpace);
	await ensureSystemInstruction(openedChannel);
	await ensureSchema(openedChannel);
	channel.set(openedChannel);
	spaceAdmin.set(openedSpace);
	setupChannelEventHandlers(openedChannel);
	seedPromptHistory(openedChannel);
	await loadAllObjects(openedChannel);
	rebuildGraph();
	refreshConversations();

	// Update URL
	updateUrlWithSpaceId(spaceId);

	// Start presence in new space
	const user = get(currentUser);
	if (user) {
		presence.start(openedChannel, openedSpace, user.id, user);
	}

	return openedChannel;
}

export async function createNewSpace(
	name: string,
): Promise<RoolChannel | null> {
	const client = get(roolClient);
	if (!client) return null;

	// Stop presence for current space
	presence.stop();

	// Close current channel
	const currentChannel = get(channel);
	if (currentChannel) {
		currentChannel.close();
	}

	// Clear caches
	clearObjectCache();

	// Create new space and open a channel on it
	const newSpace = await client.createSpace(name);
	const newChannel = await client.openChannel(newSpace.id, 'default');
	await ensureConversation(newChannel, newSpace);
	await ensureSystemInstruction(newChannel);
	await ensureSchema(newChannel);
	channel.set(newChannel);
	spaceAdmin.set(newSpace);
	setupChannelEventHandlers(newChannel);
	seedPromptHistory(newChannel);
	await loadAllObjects(newChannel);
	rebuildGraph();

	// Update URL
	updateUrlWithSpaceId(newSpace.id);

	// Start presence in new space
	const user = get(currentUser);
	if (user) {
		presence.start(newChannel, newSpace, user.id, user);
	}

	// Refresh spaces list
	await refreshSpaces();

	return newChannel;
}

export async function renameCurrentSpace(newName: string): Promise<void> {
	const currentSpace = get(spaceAdmin);
	if (!currentSpace) return;

	await currentSpace.rename(newName);
	await refreshSpaces();
}

export function refreshConversations(): void {
	const currentSpace = get(spaceAdmin);
	if (!currentSpace) return;

	const list = currentSpace.getChannels();
	conversations.set(list);
}

export async function switchConversation(
	conversationId: string,
): Promise<void> {
	const client = get(roolClient);
	const currentChannel = get(channel);
	const currentSpace = get(spaceAdmin);
	if (!client || !currentChannel || !currentSpace) return;

	// Close old channel and open a new one for the new conversation
	const spaceId = currentChannel.id;
	currentChannel.close();

	const newChannel = await client.openChannel(spaceId, conversationId);
	await ensureSystemInstruction(newChannel);
	channel.set(newChannel);
	setupChannelEventHandlers(newChannel);
	interactionsUpdateTrigger.update((n) => n + 1);
}

export async function createNewConversation(name?: string): Promise<void> {
	const client = get(roolClient);
	const currentChannel = get(channel);
	if (!client || !currentChannel) return;

	const spaceId = currentChannel.id;
	const id = crypto.randomUUID();
	await client.renameChannel(spaceId, id, name ?? 'New chat');
	await switchConversation(id);
	refreshConversations();
}

export async function deleteSpace(spaceId: string): Promise<void> {
	const client = get(roolClient);
	if (!client) return;

	const currentChannel = get(channel);

	// Delete the space
	await client.deleteSpace(spaceId);
	await refreshSpaces();

	// If we deleted the current space, switch to another one
	if (currentChannel && currentChannel.id === spaceId) {
		const spaceList = get(spaces);
		if (spaceList.length > 0) {
			await switchSpace(spaceList[0].id);
		} else {
			// Create a new default space if no spaces left
			await createNewSpace('default');
		}
	}
}
